import type { ReactNode } from 'react';
import React, { useRef, useEffect, useState, Fragment, useContext } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'Src/i18n';
import URI from 'urijs';
import { getIframeCallbackUrl } from 'Utils/payment/payment.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { useRouter } from 'next/router';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { usePaymentRedirection } from './usePaymentRedirection';
type Props = {
  children: ReactNode;
  paymentProviderId: string;
  paymentId?: string;
};
export const IframePayment = ({ children, paymentProviderId, paymentId }: Props) => {
  const [isMounted, setIsMounted] = useState(false);
  const iframe = useRef();
  const { asPath } = useRouter();
  const { redirectUri } = useContext(PaymentInfoContext);
  const appContext = useAppContext();
  const origin = new URI(appContext.originalUrl).origin();
  const location = new URI(origin + asPath);
  const iframeValidationUrl = new URI(getIframeCallbackUrl(location))
    .setQuery('callbackUrl', redirectUri)
    .setQuery('paymentId', paymentId)
    .toString();
  const { redirectToConfirmation } = usePaymentRedirection({
    paymentProviderId,
  });
  useEffect(() => {
    // ⚠️ setTimeout is mandatory for work firefox (we don't know why)
    setTimeout(() => {
      setIsMounted(true);
    }, 0);
    window.addEventListener('message', redirectToConfirmation, false);
    return () => window.removeEventListener('message', redirectToConfirmation, false);
  }, []);

  if (paymentId) {
    return (
      <iframe
        title={t('booking.banking.pageTitle')}
        ref={iframe}
        className="min-h-0"
        src={iframeValidationUrl}
      />
    );
  }

  return (
    <Fragment>
      <iframe
        data-testid="PaymentProcessIframe"
        title={t('booking.banking.pageTitle')}
        ref={iframe}
        className="min-h-1440 w-full border-0"
      />
      {isMounted &&
        iframe.current &&
        iframe.current.contentDocument && // needed for prevent bug when it's rerender and we no longer had access to the iframe (there has been a redirection)
        // @ts-expect-error TODO(flow-migration)
        createPortal(children, iframe.current.contentDocument.body)}
    </Fragment>
  );
};
